import { type UnknownAction } from "@reduxjs/toolkit";
import _ from "lodash";
import { email } from "src/localization/english/commons";

import {
  FETCH_ANSWER_FOR_SEEK_REQUEST,
  FETCH_SEARCH_ANSWER_FOR_SEEK_REQUEST,
  FETCH_ANSWER_FOR_SEEK_ASSET_REQUEST,
  FETCHING_ANSWER_FOR_SEEK_SUCCEEDED,
  FETCH_SEEK_SESSIONS_REQUEST,
  FETCHING_SEEK_SESSIONS_SUCCEEDED,
  FETCHING_SEEK_SESSIONS_FAILED,
  DELETE_SEEK_SESSION_REQUEST,
  DELETING_SEEK_SESSION_SUCCEEDED,
  DELETE_SEEK_SESSIONS_REQUEST,
  DELETING_SEEK_SESSIONS_SUCCEEDED,
  DELETING_SEEK_SESSIONS_FAILED,
  FETCH_SEEK_SESSION_REQUEST,
  FETCHING_SEEK_SESSION_SUCCEEDED,
  FETCHING_SEEK_SESSION_FAILED,
  FETCH_SEEK_EMAIL_RESPONSE,
  FETCHING_SEEK_EMAIL_RESPONSE_SUCCEEDED,
  FETCHING_SEEK_EMAIL_RESPONSE_FAILED,
  INVALIDATE_SEEK_EMAIL_RESPONSE,
  UPDATE_SEEK_FEEDBACK,
  UPDATING_SEEK_FEEDBACK_SUCCEEDED,
  FETCHING_SEEK_LIMIT_SUCCEEDED,
  FETCHING_SEEK_LIMIT_FAILED,
  FETCH_SEEK_PROMPTS,
  FETCHING_SEEK_PROMPTS_SUCCEEDED,
  FETCHING_SEEK_PROMPTS_FAILED,
  INVALIDATE_SEEK_SESSION,
  TOGGLE_SEEK,
  FETCHING_ANSWER_FOR_SEEK_FAILED,
  FETCH_ASSET_SUMMARY,
  FETCHING_ASSET_SUMMARY_SUCCEEDED,
  FETCHING_ASSET_SUMMARY_FAILED,
} from "src/redux/actions/actionTypes";

import {
  SeekAnswerRequest,
  SeekError,
  SeekAnswerResponse,
  SeekState,
  SeekHistorySessions,
  SeekEmailResponse,
  SeekAssetSummaryRequest,
  SeekAssetSummaryErrorResponse,
  SeekEventResponse,
  SeekAnswerAssetRequest,
  SeekAnswerErrorResponse,
  SeekChat,
} from "src/types/seek";

const initialSeekState: SeekState = {
  loading: false,
  success: false,
  error: {
    message: "",
    code: 0,
  },
  prompts: {
    loading: false,
    success: false,
    items: [],
    error: {
      message: "",
      code: 0,
    },
  },
  session: {
    loading: false,
    item: {
      id: "",
      name: "",
      createdDate: 0,
      chats: [],
    },
    error: {
      message: "",
      code: 0,
    },
  },
  sessions: {
    items: [],
    loading: false,
    loadMore: false,
    deleting: false,
    deleted: false,
    pagination: {
      currentPage: 0,
      totalPages: 0,
      totalRecordsCount: 0,
      recordsPerPage: 0,
      records: [],
    },
  },
  email: {
    session: "",
    id: "",
    loading: false,
    success: false,
    emailBody: "",
    subject: "",
    assets: [],
  },
  userLimit: 0,
  accountLimit: 0,
};

export function seekReducer(state = initialSeekState, action: UnknownAction) {
  switch (action.type) {
    case FETCH_ANSWER_FOR_SEEK_REQUEST:
    case FETCH_SEARCH_ANSWER_FOR_SEEK_REQUEST:
    case FETCH_ANSWER_FOR_SEEK_ASSET_REQUEST: {
      const { question, uuid } = action.payload as
        | SeekAnswerRequest
        | SeekAnswerAssetRequest;

      // If uuid is null, replace chats with only the new chat (start fresh)
      // Otherwise append the new chat to existing chats

      if (uuid === null) {
        return state;
      }

      let sessionState = state.session.item;
      if (sessionState == null) {
        sessionState = {
          id: uuid ?? "",
          name: "",
          createdDate: 0,
          chats: [],
        }
      }

      sessionState = {
        ...sessionState,
        chats: [
          ...sessionState.chats || [],
          {
            uuid: uuid ?? "",
            id: null,
            question,
            answer: "",
            citations: [],
            contents: [],
            events: [],
            session: uuid ?? "",
            creating: true,
            showPlan: true,
          }
        ],
      };

      return {
        ...state,
        loading: false,
        session: {
          ...state.session,
          item: sessionState,
          loading: true,
          error: {
            message: "",
            code: 0,
          },
        },
      };
    }

    case FETCHING_ANSWER_FOR_SEEK_SUCCEEDED: {
      const { uuid, response } = action.payload as {
        response: SeekEventResponse;
        uuid: string;
      };

      const currentSession = state.session.item;
      if (!currentSession) {
        return state;
      }

      const sessionId = response.session;

      const chats = currentSession.chats.map((chat) => {
        if (chat.uuid === uuid) {
          const type = response.type;

          let updateChat = { ...chat };

          let events = [
            ...updateChat.events,
            response,
          ];

          events = _.sortBy(events, 'time');

          if (type === 'final_answer') {
            updateChat = {
              ...updateChat,
              id: response.id ?? updateChat.id,
              session: sessionId,
              answer: response.payload.answer || updateChat.answer,
              citations: [...response.payload.citations || []],
              creating: false,
            }
          }

          return {
            ...updateChat,
            events: events,
            error: undefined,
          };
        }

        return chat;
      });

      return {
        ...state,
        loading: sessionId != null,
        session: {
          ...state.session,
          loading: false,
          item: {
            ...state.session.item,
            id: sessionId,
            chats: chats,
          },
          error: {
            message: "",
            code: 0,
          },
        },
      };
    }

    case FETCHING_ANSWER_FOR_SEEK_FAILED: {
      const { uuid, errors } = action.payload as SeekAnswerErrorResponse;
      const err = errors?.[0];
      const currentSession = state.session.item;

      if (!currentSession) {
        return {
          ...state,
          loading: false,
          session: {
            ...state.session,
            loading: false,
            error: {
              message: err?.message ?? "",
              code: typeof err?.code === "number" ? err.code : 0,
            },
          },
        };
      }

      const chats = currentSession.chats.map((chat) => {
        if (chat.uuid === uuid) {
          return {
            ...chat,
            creating: false,
            ...(err && {
              error: {
                message: err.message,
                ...(err.code !== undefined && { code: err.code }),
              },
            }),
          };
        }
        return chat;
      });

      return {
        ...state,
        loading: false,
        session: {
          ...state.session,
          loading: false,
          item: {
            ...currentSession,
            chats,
          },
          error: {
            message: "",
            code: 0,
          },
        },
      };
    }

    case FETCH_ASSET_SUMMARY: {
      const { question } = action.payload as SeekAssetSummaryRequest;
      // If id is null, replace chats with only the new chat (start fresh)
      // Otherwise append the new chat to existing chats
      const chats = [{
        uuid: null,
        question,
        answer: "",
        citations: [],
        loading: true,
      }];

      return {
        ...state,
        loading: false,
        session: {
          ...state.session,
          loading: true,
          item: {
            ...state.session.item,
            chats,
          }
        }
      };
    }

    case FETCHING_ASSET_SUMMARY_SUCCEEDED: {
      const { uuid, response } = action.payload as {
        response: SeekAnswerResponse;
        uuid: string | null;
      };

      return {
        ...state,
        loading: false,
        session: {
          ...state.session,
          loading: false,
          item: {
            ...state.session.item,
            id: response.session,
            chats: state.session.item.chats.map((chat) =>
              chat.uuid === uuid
                ? {
                  ...chat,
                  uuid: response.id,
                  session: response.session,
                  action: response.action,
                  answer: response.answer,
                  citations: response.citations,
                  loading: false,
                }
                : chat,
            ),
          },
          error: {
            message: "",
            code: 0,
          },
        },
      };
    }

    case FETCHING_ASSET_SUMMARY_FAILED: {
      const response = action.payload as any;

      return {
        ...state,
        loading: false,
        session: {
          ...state.session,
          loading: false,
          item: {
            chats: []
          },
          error: {
            message: response.errors[0].message,
            code: response.errors[0].code,
          },
        },
      };
    }

    case FETCH_SEEK_SESSIONS_REQUEST: {
      return {
        ...state,
        sessions: {
          ...state.sessions,
          loading: true,
        },
      };
    }

    case FETCHING_SEEK_SESSIONS_SUCCEEDED: {
      const response = action.payload as SeekHistorySessions;

      const updatedItems =
        response.currentPage > 0
          ? [...state.sessions.items, ...response.records]
          : response.records;

      return {
        ...state,
        sessions: {
          ...state.sessions,
          loading: false,
          items: updatedItems,
          currentPage: response.currentPage,
          totalPages: response.totalPages,
        },
      };
    }

    case FETCHING_SEEK_SESSIONS_FAILED: {
      const error = action.payload as SeekError;

      return {
        ...state,
        sessions: {
          ...state.sessions,
          loading: false,
          error: {
            message: error.message,
            code: error.code,
          },
        },
      };
    }

    case DELETE_SEEK_SESSION_REQUEST: {
      return {
        ...state,
        session: {
          ...state.session,
          deleting: true,
        },
      };
    }

    case DELETING_SEEK_SESSION_SUCCEEDED: {
      const { id } = action.payload as { id: string };
      const items = [...state.sessions.items].filter(
        (item: any) => item.id !== id,
      );

      return {
        ...state,
        sessions: {
          ...state.sessions,
          items: items,
        },
      };
    }

    case DELETE_SEEK_SESSIONS_REQUEST: {
      return {
        ...state,
        sessions: {
          ...state.sessions,
          deleting: true,
          deleted: false
        },
      };
    }

    case DELETING_SEEK_SESSIONS_SUCCEEDED: {
      return {
        ...state,
        sessions: {
          ...state.sessions,
          items: [],
          deleting: false,
          deleted: true
        },
      };
    }

    case DELETING_SEEK_SESSIONS_FAILED: {
      const error = action.payload as SeekError;

      return {
        ...state,
        sessions: {
          ...state.sessions,
          deleting: false,
          deleted: false,
          error: {
            message: error.message,
            code: error.code,
          },
        },
      };
    }

    case FETCH_SEEK_SESSION_REQUEST: {
      const { id } = action.payload as { id: string };
      return {
        ...state,
        session: {
          loading: true,
          item: {
            id: id,
            chats: [],
          },
        },
      };
    }

    case FETCHING_SEEK_SESSION_SUCCEEDED: {
      const response = action.payload as SeekHistorySessions & { records?: unknown };

      return {
        ...state,
        session: {
          ...state.session,
          loading: false,
          history: true,
          item: {
            ...state.session.item,
            name: "",
            createdDate: 0,
            chats: response.records,
          },
        },
      };
    }

    case FETCHING_SEEK_SESSION_FAILED: {
      const error = action.payload as SeekError;

      return {
        ...state,
        session: {
          ...state.session,
          loading: false,
          error: {
            message: error.message,
            code: error.code,
          },
        },
      };
    }

    case FETCH_SEEK_EMAIL_RESPONSE: {
      return {
        ...state,
        email: {
          loading: true,
          success: false,
          subject: "",
          emailBody: "",
          assets: [],
        },
      };
    }

    case FETCHING_SEEK_EMAIL_RESPONSE_SUCCEEDED: {
      const response = action.payload as SeekEmailResponse;

      return {
        ...state,
        email: {
          ...state.email,
          loading: false,
          subject: response.subject,
          emailBody: response.emailBody,
          assets: response.assets,
          success: true,
        },
      };
    }

    case FETCHING_SEEK_EMAIL_RESPONSE_FAILED: {
      const error = action.payload as SeekError;

      return {
        ...state,
        email: {
          ...state.email,
          loading: false,
          success: false,
          error: {
            message: error.message,
            code: error.code,
          },
        },
      };
    }

    case INVALIDATE_SEEK_EMAIL_RESPONSE: {
      return {
        ...state,
        email: {
          ...state.email,
          loading: false,
          success: false,
          subject: "",
          emailBody: "",
          assets: [],
        },
      };
    }

    case UPDATE_SEEK_FEEDBACK: {
      const { chatId } = action.payload as { chatId: string };

      return {
        ...state,
        session: {
          ...state.session,
          item: {
            ...state.session.item,
            chats: state.session.item.chats.map((chat) =>
              chat.uuid === chatId
                ? {
                  ...chat,
                  updating: true,
                }
                : chat,
            ),
          },
        },
      };
    }

    case UPDATING_SEEK_FEEDBACK_SUCCEEDED: {
      const response = action.payload as any;

      return {
        ...state,
        session: {
          ...state.session,
          item: {
            ...state.session.item,
            chats: state.session.item.chats.map((chat) =>
              chat.uuid === response.id
                ? {
                  ...chat,
                  helpful: response.helpful,
                  updating: false,
                }
                : chat,
            ),
          },
        },
      };
    }

    case FETCHING_SEEK_LIMIT_SUCCEEDED: {
      const response = action.payload as { userLimit: number; accountLimit: number };

      return {
        ...state,
        loading: false,
        userLimit: response.userLimit,
        accountLimit: response.accountLimit,
      };
    }

    case FETCHING_SEEK_LIMIT_FAILED: {
      const error = action.payload as SeekError;

      return {
        ...state,
        loading: false,
        error: {
          message: error.message,
          code: error.code,
        },
      };
    }

    case FETCH_SEEK_PROMPTS: {
      return {
        ...state,
        prompts: {
          ...state.prompts,
          loading: true,
        },
      };
    }

    case FETCHING_SEEK_PROMPTS_SUCCEEDED: {
      const { suggestions } = action.payload as { suggestions: Array<string> };

      return {
        ...state,
        prompts: {
          ...state.prompts,
          loading: false,
          items: suggestions,
        },
      };
    }

    case FETCHING_SEEK_PROMPTS_FAILED: {
      const error = action.payload as SeekError;

      return {
        ...state,
        prompts: {
          ...state.prompts,
          loading: false,
          error: {
            message: error.message,
            code: error.code,
          },
        },
      };
    }

    case INVALIDATE_SEEK_SESSION: {
      return {
        ...state,
        session: {
          ...state.session,
          loading: false,
          item: null,
        },
      };
    }

    case TOGGLE_SEEK: {
      return {
        ...state,
        success: !state.success,
        session: {
          ...state.session,
          item: {
            ...state.session.item,
            id: '',
          },
        },
      };
    }

    default: {
      return state;
    }
  }
}
